import React, { useEffect, useRef } from "react";
import { Stack, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AllignmentLeftIcon,
  AlignmentCenterIcon,
  AlignmentRightIcon,
} from "components/icons";
import {
  ImageAlignment,
  ImageAlignmentToolbarProps,
} from "./resizableImage.types";

interface AlignmentOption {
  value: ImageAlignment;
  icon: React.ReactNode;
  label: string;
}

export const ImageAlignmentToolbar: React.FC<ImageAlignmentToolbarProps> = ({
  currentAlignment,
  onAlignmentChange,
  onClose,
}) => {
  const theme = useTheme();
  const toolbarRef = useRef<HTMLDivElement>(null);

  const alignmentOptions: AlignmentOption[] = [
    { value: "left", icon: <AllignmentLeftIcon />, label: "Align Left" },
    { value: "center", icon: <AlignmentCenterIcon />, label: "Align Center" },
    { value: "right", icon: <AlignmentRightIcon />, label: "Align Right" },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <Stack
      ref={toolbarRef}
      direction="row"
      sx={{
        position: "absolute",
        top: -48,
        left: "50%",
        transform: "translateX(-50%)",
        p: "8px",
        border: `1px solid ${theme.palette.neutral["020"]}`,
        borderRadius: "8px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        gap: "4px",
        bgcolor: theme.palette.common.white,
        zIndex: 20,
      }}
    >
      {alignmentOptions.map((option) => (
        <Tooltip key={option.value} title={option.label} placement="top">
          <Stack
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAlignmentChange(option.value);
            }}
            sx={{
              cursor: "pointer",
              minWidth: "32px",
              minHeight: "32px",
              borderRadius: "6px",
              padding: "4px",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                currentAlignment === option.value
                  ? theme.palette.neutral["015"]
                  : "transparent",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: theme.palette.neutral["010"],
              },
            }}
          >
            {option.icon}
          </Stack>
        </Tooltip>
      ))}
    </Stack>
  );
};
